import { AfterViewInit, Component, OnInit, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormField, MatSelectChange } from '@angular/material/select';
import { MatSidenav } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, RowSelectedEvent, RowSelectionOptions, themeMaterial } from 'ag-grid-community';
import { Collection, CommonDimensions } from '../models/collection.interface';
import { CollectionsService } from '../services/collections.service';
import { CreateForwardingRuleRequest, ForwardingRuleService } from '../services/forwarding-rule.service';
import { LibrariesService, LibraryDocument } from '../services/libraries.service';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import {
  CloneDocumentsDialogComponent,
  CloneDialogData,
  CloneDialogResult
} from './clone-documents-dialog/clone-documents-dialog.component';

/**
 * CURRENT IMPLEMENTATION: Filter types for the Libraries component
 *
 * Currently supports filtering by States and Years.
 *
 * FUTURE IMPLEMENTATION:
 * When more advanced filtering is implemented, this should be extended to include:
 * - 'FilingTypes' for filtering by regulatory filing types
 * - 'Utilities' for filtering by utility companies
 * - 'Industries' for filtering by industry sectors (when expanded beyond energy)
 * - 'DateRanges' for custom date range filtering
 * - 'Tags' for user-defined or AI-generated document tags
 */
type FilterKey = 'States' | 'Years';

/**
 * CURRENT IMPLEMENTATION: Format ingestion timestamp for display
 *
 * This utility function converts ISO timestamps into human-readable relative time
 * buckets for the AG Grid display.
 *
 * CURRENT BEHAVIOR:
 * - Shows "Last hour", "Last 24 hours", "Last 7 days", "Last 30 days"
 * - Falls back to formatted date for older documents
 * - Handles invalid dates gracefully with a placeholder
 *
 * FUTURE ENHANCEMENTS:
 * - Add more granular time buckets (e.g., "Last 2 hours", "Last 12 hours")
 * - Support internationalization for different locales
 * - Add configuration for custom time bucket preferences
 * - Include processing status indicators (e.g., "Processing...", "Failed")
 *
 * @param isoOrDate ISO timestamp string or Date object
 * @returns Human-readable time bucket string
 */
function formatIngestedBucket(isoOrDate: string | Date): string {
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return 'Ã¢â‚¬â€';
  const now = Date.now();
  const diff = now - d.getTime();

  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  const d7 = 7 * day;
  const d30 = 30 * day;

  if (diff < hr) return 'Last hour';
  if (diff < day) return 'Last 24 hours';
  if (diff < d7) return 'Last 7 days';
  if (diff < d30) return 'Last 30 days';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

type ColumnKey = 'state' | 'company' | 'proceeding' | 'type' | 'year';
type MatchOp = 'equals' | 'contains' | 'starts';

interface RuleCondition {
  column_name: ColumnKey;
  operator: MatchOp;
  value: string;
}

/**
 * CURRENT IMPLEMENTATION: DocRow interface for AG Grid display
 *
 * This interface defines the structure of document rows as displayed in the AG Grid.
 * It's designed to be compatible with the grid's column definitions and filtering
 * capabilities while providing all necessary document metadata.
 *
 * CURRENT FIELDS:
 * - Core identification: state, utility, filingType, proceeding
 * - Timestamps: ingestedAt, filingDate
 * - Content: title, summary
 * - Optional metadata: entity, type, industry, company, year
 *
 * FUTURE ENHANCEMENTS:
 * When the full S3 ingestion pipeline is implemented, this interface should be
 * extended to include:
 * - documentId: Unique identifier for direct document access
 * - libraryId: Reference to parent library
 * - documentUrl: Direct S3 link for document download
 * - fileSize: Document size for display and sorting
 * - pageCount: Number of pages for document preview
 * - processingStatus: Status of document processing pipeline
 * - confidence: AI confidence scores for extracted metadata
 * - tags: User-defined or AI-generated categorization tags
 * - lastModified: Document update timestamp
 * - version: Document version for change tracking
 * - thumbnailUrl: Preview thumbnail for document cards
 */
interface DocRow {
  state: string; // State abbreviation (e.g., "CA", "TX")
  utility: string; // Utility company name
  filingType: string; // Type of regulatory filing
  proceeding: {
    // Proceeding information object
    number: string; // Unique proceeding identifier
  };
  ingestedAt: string; // ISO timestamp when document was ingested
  title: string; // Document title (generated or extracted)
  entity?: string; // Optional entity information
  type?: string; // Optional document type classification
  industry?: string; // Industry classification (defaults to "Energy")
  company?: string; // Company name (usually same as utility)
  year?: number; // Year extracted from filing date
  summary?: string; // Document summary (generated or extracted)
  filingDate?: string; // Original filing date from regulatory body
  // Document identifiers for cloning operations
  documentId?: string; // Actual document ID from database
  libraryId?: string; // Library ID this document belongs to
  fileId?: string; // File ID for this document
}

/**
 * CURRENT IMPLEMENTATION: LibrariesComponent
 *
 * This component provides the main user interface for browsing and filtering
 * library documents. It displays documents in an AG Grid with advanced filtering
 * capabilities and handles data loading from the Libraries API.
 *
 * CURRENT FUNCTIONALITY:
 * - Displays all library documents in a sortable, filterable AG Grid
 * - Provides top-level filters for States and Years
 * - Supports text search across document fields
 * - Handles document selection and row interactions
 * - Shows loading states and error handling with fallback to mock data
 * - Formats timestamps into human-readable relative time buckets
 *
 * CURRENT DATA FLOW:
 * 1. Component loads and calls getAllLibraryDocuments() from LibrariesService
 * 2. API returns documents from /libraries/all-documents endpoint
 * 3. Backend scans DynamoDB table and transforms library items to documents
 * 4. Frontend transforms API response to DocRow format for AG Grid
 * 5. User can filter and search through the displayed documents
 *
 * FUTURE IMPLEMENTATION CONSIDERATIONS:
 * When the full S3 ingestion pipeline and advanced features are implemented,
 * this component should be enhanced to support:
 * - Real-time document updates via WebSocket connections
 * - Advanced search with semantic/vector similarity
 * - Document preview and thumbnail display
 * - Bulk operations (copy, move, delete, export)
 * - Saved searches and user preferences
 * - Document upload and management interfaces
 * - Processing status indicators for ingested documents
 * - Advanced filtering by filing type, utility company, etc.
 * - Pagination for large document sets
 * - Document clustering and categorization views
 * - Integration with document processing pipeline status
 */
@Component({
  selector: 'app-libraries',
  templateUrl: './libraries.component.html',
  styleUrls: ['./libraries.component.scss']
})
export class LibrariesComponent implements OnInit, AfterViewInit {
  // UI state management
  showFilters = false; // Toggle for filter panel visibility
  @ViewChild('agGrid') agGrid!: AgGridAngular; // Reference to AG Grid component
  @ViewChild('rulesSidenav') rulesSidenav!: MatSidenav;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChildren(MatFormField) formFields!: QueryList<MatFormField>;
  selectedRows: DocRow[] = []; // Currently selected document rows
  theme = themeMaterial; // AG Grid theme
  isLoading = false; // Loading state for API calls
  forwardingRuleForm!: FormGroup;

  constructor(
    private librariesService: LibrariesService,
    private collectionsService: CollectionsService,
    private fb: FormBuilder,
    private forwardingRuleService: ForwardingRuleService,
    private snackBar: MatSnackBar,
    private renderer: Renderer2,
    private dialog: MatDialog
  ) {
    this.initialState();
  }

  initialState() {
    this.forwardingRuleForm = this.fb.group(
      {
        name: this.fb.control('', {
          validators: [Validators.required, Validators.minLength(1), Validators.maxLength(255)],
          updateOn: 'change'
        }),
        logic_operator: this.fb.control('', {
          validators: [this.conditionalLogicValidator()],
          updateOn: 'change'
        }),
        conditions: this.fb.array([this.createConditionFormGroup()], {
          validators: [Validators.required, this.minConditionsValidator],
          updateOn: 'change'
        }),
        target_collection_id: this.fb.control('', {
          validators: [Validators.required],
          updateOn: 'change'
        }),
        frequency: this.fb.control('pastAndFuture', {
          validators: [Validators.required],
          updateOn: 'change'
        }),
        organization_id: this.fb.control('org_uuid', {
          validators: [Validators.required],
          updateOn: 'change'
        })
      },
      { updateOn: 'change' }
    );
  }

  private createConditionFormGroup(): FormGroup {
    return this.fb.group({
      column_name: ['state', Validators.required],
      operator: ['equals', Validators.required],
      value: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  private minConditionsValidator(control: AbstractControl) {
    const conditions = control as FormArray;
    if (!conditions || conditions.length === 0) {
      return { minConditions: true };
    }

    const validConditions = conditions.controls.filter(conditionControl => {
      const condition = conditionControl.value;
      return condition.column_name && condition.operator && condition.value?.trim();
    });

    return validConditions.length > 0 ? null : { minConditions: true };
  }

  get conditionsFormArray(): FormArray {
    return this.forwardingRuleForm.get('conditions') as FormArray;
  }

  closeRules() {
    this.resetForwardingRuleForm();
    this.rulesSidenav?.close();
  }

  openRules() {
    this.rulesSidenav?.open();
  }

  toggleRules() {
    this.rulesSidenav?.toggle();
  }

  // Filter state management
  pendingFilterChanges = false; // Track unsaved filter changes
  columnFiltersDirty = false; // Track AG Grid column filter state

  isTargetAutocompleteOpen = false;
  isColumnAutocompleteOpen = false;

  columnOptions: { value: ColumnKey; label: string }[] = [
    { value: 'state', label: 'State' },
    { value: 'company', label: 'Company' },
    { value: 'proceeding', label: 'Proceeding Number' },
    { value: 'type', label: 'Filing Type' },
    { value: 'year', label: 'Year' }
  ];

  /**
   * CURRENT IMPLEMENTATION: Count active top-level filters
   *
   * This getter calculates how many filter categories have active selections
   * (excluding "All" selections). Used to display filter count in UI.
   *
   * FUTURE ENHANCEMENT: When more filter types are added, this will
   * automatically include them in the count calculation.
   */
  get activeFilterCount(): number {
    return this.filterTypes.reduce((acc, key) => {
      const sel = this.selectedFilters[key] ?? [];
      // Active if not just ALL_TOKEN
      return acc + (sel.length === 1 && sel[0] === this.ALL_TOKEN ? 0 : sel.length ? 1 : 0);
    }, 0);
  }

  /**
   * CURRENT IMPLEMENTATION: AG Grid row selection configuration
   *
   * Enables multi-row selection with click selection and select-all functionality.
   * This allows users to select multiple documents for future bulk operations.
   *
   * FUTURE ENHANCEMENT: When bulk operations are implemented, this selection
   * will enable actions like bulk copy, move, delete, or export.
   */
  rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'multiRow',
    selectAll: 'filtered',
    enableClickSelection: true
  };

  /**
   * CURRENT IMPLEMENTATION: Default column configuration for AG Grid
   *
   * Sets up consistent behavior across all columns including sorting, resizing,
   * and text filtering with debounced input for performance.
   */
  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 140,
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter', // Default to text filter
    filterParams: {
      debounceMs: 200, // Debounce filter input for performance
      buttons: ['reset'] // Add reset button to filters
    }
  };

  /**
   * CURRENT IMPLEMENTATION: AG Grid column definitions
   *
   * Defines the structure and behavior of each column in the document grid.
   * Each column is configured with appropriate filters, formatters, and sorting.
   *
   * CURRENT COLUMNS:
   * - Ingested: Shows relative time buckets with date filter
   * - State: State abbreviation with text filter
   * - Company: Utility company name with text filter
   * - Proceeding #: Proceeding number with text filter
   * - Year: Filing year with numeric filter
   * - Title: Document title with text filter and tooltip
   * - Summary: Document summary with text filter and tooltip
   *
   * FUTURE ENHANCEMENTS:
   * When the full document processing pipeline is implemented, additional
   * columns should be added:
   * - File Size: Document size with numeric filter
   * - Pages: Page count with numeric filter
   * - Status: Processing status with custom filter
   * - Tags: Document tags with multi-select filter
   * - Confidence: AI confidence scores with numeric filter
   * - Actions: Download, preview, edit buttons
   */
  colDefs: ColDef[] = [
    {
      field: 'ingestedAt',
      headerName: 'Ingested',
      flex: 1,
      valueFormatter: p => formatIngestedBucket(p.value),
      tooltipValueGetter: p => new Date(p.data?.ingestedAt).toLocaleString(),
      comparator: (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      sort: 'desc', // Default sort by newest first
      filter: 'agDateColumnFilter',
      filterParams: {
        // Custom comparator for ISO string dates
        comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          // Normalize to midnight for comparison
          const pick = new Date(
            filterLocalDateAtMidnight.getFullYear(),
            filterLocalDateAtMidnight.getMonth(),
            filterLocalDateAtMidnight.getDate()
          ).getTime();
          const cell = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()).getTime();
          return cell === pick ? 0 : cell < pick ? -1 : 1;
        },
        debounceMs: 200
      }
    },
    { field: 'state', headerName: 'State', flex: 0.8 },
    { field: 'company', headerName: 'Company', flex: 1 },
    { field: 'proceeding.number', headerName: 'Proceeding #', flex: 1, filter: 'agTextColumnFilter' },
    {
      field: 'year',
      headerName: 'Year',
      width: 110,
      sortable: true,
      filter: 'agNumberColumnFilter', // Numeric filter for years
      valueGetter: p => p.data?.year ?? null // Ensure numeric comparison
    },
    { field: 'title', headerName: 'Title', flex: 2, tooltipField: 'title' },
    {
      headerName: 'Summary',
      field: 'summary',
      flex: 2,
      filter: 'agTextColumnFilter',
      tooltipField: 'summary'
    }
  ];

  /**
   * CURRENT IMPLEMENTATION: Filter configuration
   *
   * Defines the available filter types and their options. Currently supports
   * filtering by States and Years with predefined option lists.
   *
   * FILTER STATE MANAGEMENT:
   * - filterTypes: Ordered list of filter categories
   * - selectedFilters: Currently selected values for each filter
   * - filterOptions: Available options (currently unused, replaced by baseOptions)
   * - baseOptions: Hardcoded filter options (should be replaced with API data)
   * - ALL_TOKEN: Special token to represent "All" selections
   *
   * FUTURE ENHANCEMENTS:
   * - Replace hardcoded baseOptions with dynamic API data
   * - Add more filter types (FilingTypes, Utilities, Industries, etc.)
   * - Implement faceted search with result counts for each option
   * - Add date range filters and custom filter builders
   * - Support saved filter presets and user preferences
   */
  filterTypes: FilterKey[] = ['States', 'Years'];

  selectedFilters: Record<string, string[]> = {
    States: ['All States'],
    Years: ['All Years']
  };

  filterOptions: Record<string, string[]> = {
    States: ['All States', 'CA', 'TX'],
    Years: ['All Years', '2023', '2024']
  };

  // TODO: Replace with dynamic API data when backend supports filter options endpoint
  baseOptions: Record<FilterKey, string[]> = {
    States: ['CA', 'TX', 'IL', 'FL', 'WA', 'NY', 'CO', 'AZ', 'NV', 'OR', 'ID'],
    Years: ['2025', '2024', '2023', '2022', '2021', '2020', '2019', '2018']
  };

  readonly ALL_TOKEN = '__ALL__';

  pastAndFuture: 'pastAndFuture' | 'future' = 'pastAndFuture';
  matchOperator: MatchOp = 'equals';

  collections: Collection[] = [];
  collectionInput: Collection | null = {} as Collection; // Input for autocomplete
  filteredCollections: Collection[] = []; // Filtered collections for autocomplete
  selectedCollection: Collection | undefined = undefined;

  /**
   * Check if a filter type has no real selections (only ALL_TOKEN or empty)
   */
  isEmpty(ft: FilterKey): boolean {
    const sel = this.selectedFilters[ft] ?? [];
    return sel.filter(v => v !== this.ALL_TOKEN).length === 0;
  }

  /**
   * CURRENT IMPLEMENTATION: Component initialization
   *
   * Initializes filter state and loads library documents on component startup.
   * Sets up default "All" selections for each filter type and triggers data loading.
   *
   * CURRENT BEHAVIOR:
   * - Initializes all filters to "All" state (ALL_TOKEN + all available options)
   * - Calls loadLibraryDocuments() to fetch data from API
   *
   * FUTURE ENHANCEMENTS:
   * - Load user preferences for default filter selections
   * - Support URL parameters for deep linking to filtered views
   * - Initialize with saved search queries or bookmarked filters
   */
  ngOnInit(): void {
    this.getCollections();
    this.filterTypes.forEach(key => {
      const allReal = this.baseOptions[key] ?? [];
      this.selectedFilters[key] = [this.ALL_TOKEN, ...allReal];
    });
    this.loadLibraryDocuments();
  }

  ngAfterViewInit() {
    if (this.forwardingRuleForm) {
      this.setupFormValidationWatcher();
    }
  }

  cleanInvalidClassesOnFormFields(): void {
    if (!this.formFields) return;

    this.formFields.forEach(formField => {
      const formFieldElement = formField._elementRef.nativeElement;

      // SEARCH INPUTS AND SELECTS
      const inputElement = formFieldElement.querySelector('input') as HTMLInputElement;
      const selectElement = formFieldElement.querySelector('mat-select') as HTMLElement;

      const element = inputElement || selectElement;
      if (!element) return;

      const textFieldWrapper = formFieldElement.querySelector('.mdc-text-field');
      if (!textFieldWrapper) return;

      // GET CONTROL NAME OF THE ELEMENT
      let formControlName = '';

      if (inputElement) {
        formControlName =
          inputElement.getAttribute('formcontrolname') || inputElement.getAttribute('ng-reflect-name') || '';
      } else if (selectElement) {
        formControlName = selectElement.getAttribute('ng-reflect-name') || '';
      }

      if (formControlName) {
        let control = null;
        control = this.forwardingRuleForm.get(formControlName);
        if (!control) {
          // SEARCH IN EACH CONDITION FROM FormArray
          for (let i = 0; i < this.conditionsFormArray.length; i++) {
            const conditionGroup = this.conditionsFormArray.at(i) as FormGroup;
            const conditionControl = conditionGroup.get(formControlName);
            if (conditionControl) {
              control = conditionControl;
              break;
            }
          }
        }

        if (!control) {
          const conditionItem = element.closest('.condition-item');
          if (conditionItem) {
            const conditionItems = Array.from(conditionItem.parentElement?.children || []);
            const conditionIndex = conditionItems.indexOf(conditionItem);

            if (conditionIndex >= 0 && conditionIndex < this.conditionsFormArray.length) {
              const conditionGroup = this.conditionsFormArray.at(conditionIndex) as FormGroup;
              control = conditionGroup.get(formControlName);
            }
          }
        }

        if (control) {
          if (control.pristine && !control.touched) {
            this.renderer.removeClass(textFieldWrapper, 'mdc-text-field--invalid');
            console.log(
              `Removed invalid class from ${formControlName} (${control.pristine ? 'pristine' : 'dirty'}, ${control.touched ? 'touched' : 'untouched'})`
            );
          } else if (control.touched && control.invalid) {
            this.renderer.addClass(textFieldWrapper, 'mdc-text-field--invalid');
            console.log(`Added invalid class to ${formControlName} (touched and invalid)`);
          }
        } else {
          console.warn(`Could not find FormControl for field: ${formControlName}`);
        }
      }
    });
  }

  cleanInvalidClassesForCondition(conditionIndex: number): void {
    if (!this.formFields) return;

    const conditionGroup = this.conditionsFormArray.at(conditionIndex) as FormGroup;
    if (!conditionGroup) return;

    // Specific fields for each condition
    const conditionFields = ['column_name', 'operator', 'value'];

    conditionFields.forEach(fieldName => {
      const control = conditionGroup.get(fieldName);
      if (!control) return;

      this.formFields.forEach(formField => {
        const formFieldElement = formField._elementRef.nativeElement;

        // Check if this form field is within the specific condition
        const conditionItem = formFieldElement.closest('.condition-item');
        if (!conditionItem) return;

        const conditionItems = Array.from(conditionItem.parentElement?.children || []);
        const itemIndex = conditionItems.indexOf(conditionItem);

        if (itemIndex !== conditionIndex) return;

        // Search for the specific field element
        const inputElement = formFieldElement.querySelector(
          `input[formcontrolname="${fieldName}"]`
        ) as HTMLInputElement;
        const selectElement = formFieldElement.querySelector(
          `mat-select[ng-reflect-name="${fieldName}"]`
        ) as HTMLElement;

        if (inputElement || selectElement) {
          const textFieldWrapper = formFieldElement.querySelector('.mdc-text-field');
          if (textFieldWrapper) {
            if (control.pristine && !control.touched) {
              this.renderer.removeClass(textFieldWrapper, 'mdc-text-field--invalid');
            } else if (control.touched && control.invalid) {
              this.renderer.addClass(textFieldWrapper, 'mdc-text-field--invalid');
            }
          }
        }
      });
    });
  }

  cleanInvalidClassForField(fieldName: string): void {
    if (!this.formFields) return;

    const control = this.forwardingRuleForm.get(fieldName);
    if (!control) return;

    this.formFields.forEach(formField => {
      const formFieldElement = formField._elementRef.nativeElement;
      const input = formFieldElement.querySelector(
        `input[formcontrolname="${fieldName}"], input[ng-reflect-name="${fieldName}"]`
      ) as HTMLInputElement;

      if (input) {
        const textFieldWrapper = input.closest('.mdc-text-field');
        if (textFieldWrapper) {
          if (control.pristine && !control.touched) {
            this.renderer.removeClass(textFieldWrapper, 'mdc-text-field--invalid');
          } else if (control.touched && control.invalid) {
            this.renderer.addClass(textFieldWrapper, 'mdc-text-field--invalid');
          }
        }
      }
    });
  }

  setupFormValidationWatcher(): void {
    // Subscribe to form status changes
    this.forwardingRuleForm.statusChanges.subscribe(() => {
      // Use setTimeout to allow Angular Material to finish its processes
      setTimeout(() => {
        this.cleanInvalidClassesOnFormFields();
      }, 50);
    });

    // Also monitor value changes
    this.forwardingRuleForm.valueChanges.subscribe(() => {
      setTimeout(() => {
        this.cleanInvalidClassesOnFormFields();
      }, 50);
    });
  }

  isFormValid(): boolean {
    const formValid = this.forwardingRuleForm.valid;
    return formValid;
  }

  resetForwardingRuleForm(): void {
    console.log('Resetting form with CSS class management...');

    // 1. Clean all invalid classes before reset
    this.cleanInvalidClassesOnFormFields();

    // 2. Reset the FormGroup
    this.forwardingRuleForm.reset();

    // 3. Clear the conditions FormArray
    const conditionsArray = this.conditionsFormArray;
    while (conditionsArray.length > 0) {
      conditionsArray.removeAt(0);
    }

    // 4. Recreate the form
    this.forwardingRuleForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(255)]],
      logic_operator: ['', [this.conditionalLogicValidator()]],
      conditions: this.fb.array([this.createConditionFormGroup()], [Validators.required, this.minConditionsValidator]),
      target_collection_id: ['', Validators.required],
      frequency: ['pastAndFuture', Validators.required],
      organization_id: ['org_uuid', Validators.required]
    });

    // 5. Clear related variables
    this.collectionInput = null;
    this.selectedCollection = undefined;
    this.filteredCollections = [...this.collections];
    this.isTargetAutocompleteOpen = false;
    this.isColumnAutocompleteOpen = false;

    // 6. Setup watcher for the new form
    this.setupFormValidationWatcher();

    // 7. Clean classes after reset
    setTimeout(() => {
      this.cleanInvalidClassesOnFormFields();
    }, 100);

    // 8. Second pass to ensure cleanup
    setTimeout(() => {
      this.cleanInvalidClassesOnFormFields();
    }, 200);
  }

  createForwardingRule(): void {
    if (this.isFormValid()) {
      const formValue = this.forwardingRuleForm.value;

      const forwardingRuleModel: CreateForwardingRuleRequest = {
        name: formValue.name,
        logic_operator: formValue.logic_operator,
        conditions: formValue.conditions.map((condition: RuleCondition) => ({
          column_name: condition.column_name,
          operator: condition.operator,
          value: condition.value
        })),
        target_collection_id: formValue.target_collection_id,
        frequency: formValue.frequency,
        organization_id: formValue.organization_id
      };

      console.log('Creating forwarding rule:', forwardingRuleModel);

      this.forwardingRuleService.createForwardingRule(forwardingRuleModel).subscribe({
        next: response => {
          this.snackBar.open(`Forwarding Rule "${response.name}" created`, 'Close', {
            duration: 5000
          });
          this.resetForwardingRuleForm();
          this.closeRules();
        },
        error: error => {
          console.error('Error creating forwarding rule:', error);
          this.snackBar.open(`Error creating the forwarding rule`, 'Close', {
            duration: 5000
          });
        }
      });
    }
  }

  /**
   * CURRENT IMPLEMENTATION: Load library documents from API
   *
   * This method fetches all library documents using the LibrariesService and handles
   * the response by transforming the data for AG Grid display. It includes error
   * handling with fallback to mock data for development and testing.
   *
   * CURRENT DATA FLOW:
   * 1. Set loading state to true
   * 2. Call getAllLibraryDocuments() from LibrariesService
   * 3. Transform API response to DocRow format for AG Grid
   * 4. Apply current filters to the loaded data
   * 5. Set loading state to false
   *
   * ERROR HANDLING:
   * - Logs errors to console for debugging
   * - Falls back to mock data to ensure UI remains functional
   * - Always sets loading state to false to prevent stuck loading indicators
   *
   * FUTURE ENHANCEMENTS:
   * - Add retry logic for failed API calls
   * - Implement caching to reduce API calls
   * - Add real-time updates via WebSocket connections
   * - Support incremental loading for large document sets
   * - Add user notifications for loading errors
   * - Implement offline support with cached data
   */
  private loadLibraryDocuments(): void {
    this.isLoading = true;
    // Use the new all-documents endpoint to get all library documents
    this.librariesService.getAllLibraryDocuments().subscribe({
      next: response => {
        console.log('DEBUG: Full API response:', response);
        console.log('DEBUG: Number of documents in response:', response.documents?.length || 0);
        console.log('DEBUG: Documents array:', response.documents);

        this.allRowData = this.transformApiDataToDocRows(response.documents);
        console.log('DEBUG: Transformed row data count:', this.allRowData.length);
        console.log('DEBUG: Transformed row data:', this.allRowData);

        this.applyFilters();
        console.log('DEBUG: Filtered row data count:', this.filteredRowData.length);
        console.log('DEBUG: Filtered row data:', this.filteredRowData);

        this.isLoading = false;
      },
      error: error => {
        console.error('Error loading library documents:', error);
        // Fall back to mock data on error
        this.allRowData = this.getMockData();
        this.applyFilters();
        this.isLoading = false;
      }
    });
  }

  /**
   * CURRENT IMPLEMENTATION: Transform API response to AG Grid format
   *
   * This method converts LibraryDocument objects from the API into DocRow objects
   * that are compatible with the AG Grid display. It handles missing fields gracefully
   * and ensures consistent data structure for the grid.
   *
   * CURRENT TRANSFORMATIONS:
   * - Maps utility/company fields with fallbacks
   * - Handles optional fields with default values
   * - Ensures proceeding number is in object format for grid
   * - Provides fallback timestamp for missing ingestedAt
   *
   * FUTURE ENHANCEMENTS:
   * When the full S3 ingestion pipeline is implemented, this method should:
   * - Handle additional metadata fields (fileSize, pageCount, etc.)
   * - Process document processing status indicators
   * - Transform vector embedding metadata for search relevance
   * - Handle document versioning and update information
   * - Process AI confidence scores and quality indicators
   * - Transform document tags and categorization data
   *
   * @param documents Array of LibraryDocument objects from API
   * @returns Array of DocRow objects for AG Grid display
   */
  private transformApiDataToDocRows(documents: LibraryDocument[]): DocRow[] {
    return documents.map(doc => ({
      state: doc.state || '',
      utility: doc.utility || doc.company || '',
      filingType: doc.filingType || '',
      proceeding: { number: doc.proceeding?.number || '' },
      ingestedAt: doc.ingestedAt || new Date().toISOString(),
      title: doc.title || '',
      company: doc.company || doc.utility || '',
      year: doc.year || undefined,
      summary: doc.summary || '',
      filingDate: doc.filingDate || undefined,
      // Include document identifiers for cloning
      documentId: doc.documentId,
      libraryId: doc.libraryId,
      fileId: doc.fileId
    }));
  }

  /**
   * CURRENT IMPLEMENTATION: Mock data for development and fallback
   *
   * Provides sample library documents for development, testing, and error fallback.
   * This data represents the expected structure and variety of real library documents.
   *
   * MOCK DATA INCLUDES:
   * - Various utility companies (SDG&E, PG&E, TXU)
   * - Different filing types (Wildfire Mitigation Plan, General Rate Case)
   * - Multiple states (CA, TX)
   * - Different time periods for ingestion timestamps
   * - Realistic proceeding numbers and document titles
   * - Sample summaries representing document content
   *
   * FUTURE CONSIDERATIONS:
   * - This mock data should be removed once the API is stable (!!!)
   * - Consider moving to a separate mock data service for testing
   * - Add more diverse examples when expanding beyond energy sector
   * - Include examples of different document processing statuses
   *
   * @returns Array of sample DocRow objects
   */
  private getMockData(): DocRow[] {
    return [
      {
        state: 'CA',
        utility: 'SDG&E',
        filingType: 'Wildfire Mitigation Plan',
        proceeding: { number: 'A2505009' },
        ingestedAt: new Date(Date.now() - 45 * 60_000).toISOString(),
        title: 'CaliforniaÃ¢â‚¬â€œEnergyÃ¢â‚¬â€œReport.pdf',
        entity: 'Green Future Energy',
        type: 'Energy Report',
        industry: 'Energy',
        company: 'Green Future LLC',
        summary: 'Mitigation plan proceeding for 2024 covering wildfire safety strategies',
        year: 2025,
        documentId: 'document#mock-doc-1',
        libraryId: 'library#mock-lib-1',
        fileId: 'file#mock-file-1'
      },
      {
        state: 'CA',
        utility: 'PG&E',
        filingType: 'General Rate Case',
        proceeding: { number: 'A2403001' },
        ingestedAt: new Date(Date.now() - 3 * 24 * 3600_000).toISOString(),
        title: 'PG&E GRC Application.pdf',
        entity: 'PG&E',
        type: 'GRC',
        industry: 'Energy',
        company: 'PG&E',
        summary: 'GRC filing for 2024 including rate adjustments and infrastructure investments',
        year: 2024,
        documentId: 'document#mock-doc-2',
        libraryId: 'library#mock-lib-2',
        fileId: 'file#mock-file-2'
      },
      {
        state: 'TX',
        utility: 'TXU',
        filingType: 'General Rate Case',
        proceeding: { number: 'A2301002' },
        ingestedAt: '2024-02-15T12:00:00Z',
        title: 'TXU Rate Case Ã¢â‚¬â€œ Filing',
        entity: 'TXU',
        type: 'GRC',
        industry: 'Energy',
        company: 'TXU',
        summary: 'Rate case filing for 2023 focusing on grid reliability improvements',
        year: 2023,
        documentId: 'document#mock-doc-3',
        libraryId: 'library#mock-lib-3',
        fileId: 'file#mock-file-3'
      }
    ];
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  allLabel(key: FilterKey): string {
    return `All ${this.labelFor(key)}`;
  }

  labelFor(key: FilterKey): string {
    return key;
  }

  toggleAll(ft: FilterKey, ev: MatOptionSelectionChange) {
    if (!ev.isUserInput) return;
    const allReal = this.baseOptions[ft] ?? [];

    Promise.resolve().then(() => {
      if (ev.source.selected) {
        // Show "All ..." as selected AND check every real option
        this.selectedFilters[ft] = [this.ALL_TOKEN, ...allReal];
      } else {
        // Unselecting "All ..." -> clear all (or leave empty to force a choice)
        this.selectedFilters[ft] = [];
      }

      // mark pending
      this.pendingFilterChanges = true;
    });
  }

  toggleSpecific(ft: FilterKey, option: string, ev: MatOptionSelectionChange) {
    if (!ev.isUserInput) return;
    const allReal = this.baseOptions[ft] ?? [];
    let sel = [...(this.selectedFilters[ft] ?? [])];

    if (ev.source.selected) {
      sel = sel.filter(v => v !== this.ALL_TOKEN);
      if (!sel.includes(option)) sel.push(option);
    } else {
      sel = sel.filter(v => v !== option);
    }

    const withoutAll = sel.filter(v => v !== this.ALL_TOKEN);

    const finalSel =
      withoutAll.length === allReal.length && allReal.length > 0 ? [this.ALL_TOKEN, ...withoutAll] : withoutAll;

    Promise.resolve().then(() => {
      this.selectedFilters[ft] = finalSel;
      this.pendingFilterChanges = true;
    });
  }

  onFilterChange(filterType: FilterKey, event: MatSelectChange) {
    const allReal = this.baseOptions[filterType] ?? [];

    const prev = this.selectedFilters[filterType] ?? [];
    const next = [...(event.value ?? [])];

    const hadAll = prev.includes(this.ALL_TOKEN);
    const hasAll = next.includes(this.ALL_TOKEN);

    if (!hadAll && hasAll) {
      this.selectedFilters[filterType] = [this.ALL_TOKEN, ...allReal];
      return;
    }

    if (hadAll && !hasAll) {
      this.selectedFilters[filterType] = [];
      return;
    }

    if (hasAll) {
      const realCount = next.filter(v => v !== this.ALL_TOKEN).length;
      if (realCount < allReal.length) {
        this.selectedFilters[filterType] = next.filter(v => v !== this.ALL_TOKEN);
        return;
      }
    }

    const cleaned = next.filter(v => v !== this.ALL_TOKEN);
    if (cleaned.length === allReal.length && allReal.length > 0) {
      this.selectedFilters[filterType] = [this.ALL_TOKEN, ...cleaned];
    } else {
      this.selectedFilters[filterType] = cleaned;
    }
  }

  // Helper for the trigger text
  displayTriggerText(ft: FilterKey): string {
    const sel = this.selectedFilters[ft] ?? [];
    if (sel.includes(this.ALL_TOKEN)) return this.allLabel(ft);
    const real = sel.filter(v => v !== this.ALL_TOKEN);
    return real.length ? real.join(', ') : 'Ã¢â‚¬â€ Select at least one Ã¢â‚¬â€';
  }

  // Map FilterKey to the field on DocRow
  private filterField(key: FilterKey): keyof DocRow {
    switch (key) {
      case 'States':
        return 'state';
      case 'Years':
        return 'year';
    }
  }

  // Normalize a row value to compare against selections
  private normalizeForCompare(
    key: FilterKey,
    rowValue: string | number | { number: string; description?: string } | undefined
  ): string {
    if (key === 'Years') return String(rowValue ?? '');
    if (typeof rowValue === 'object' && rowValue !== null && 'number' in rowValue) {
      return String(rowValue.number ?? '').trim();
    }
    return String(rowValue ?? '').trim();
  }

  // Build a Set of selected values for a given filter
  private selectedSet(key: FilterKey): Set<string> {
    const sel = this.selectedFilters[key] ?? [];
    // store ALL_TOKEN + all real options when "All ..." is active.
    const real = sel.filter(v => v !== this.ALL_TOKEN);
    return new Set(real);
  }

  onSearchChange(v: string) {
    this.gridFilterText = v ?? '';
    this.applyFilters(); // reuse existing pipeline
  }

  private textContains(haystack: unknown, needle: string): boolean {
    if (!needle) return true; // no keyword constraint
    const h = (haystack ?? '').toString().toLowerCase();
    return h.includes(needle.toLowerCase());
  }

  private rowMatchesKeyword(row: DocRow, q: string): boolean {
    if (!q) return true;
    // searchable fields:
    return (
      this.textContains(row.title, q) ||
      this.textContains(row.company, q) ||
      this.textContains(row.state, q) ||
      this.textContains(row.year, q) ||
      this.textContains(row.summary, q) ||
      this.textContains(row.filingType, q) ||
      this.textContains(row.filingDate, q) ||
      this.textContains(row.proceeding?.number, q)
    );
  }

  applyFilters(): void {
    const sets = {
      States: this.selectedSet('States'),
      Years: this.selectedSet('Years')
    } as const;

    const q = (this.gridFilterText || '').trim();

    console.log('DEBUG: Filter sets:', sets);
    console.log('DEBUG: Selected filters:', this.selectedFilters);

    this.filteredRowData = this.allRowData.filter(row => {
      // Check if "All" is selected for each filter type
      const statesSelected = this.selectedFilters['States'] || [];
      const yearsSelected = this.selectedFilters['Years'] || [];

      // If "All" token is present, don't filter by that criteria
      const shouldFilterStates = !statesSelected.includes(this.ALL_TOKEN) && statesSelected.length > 0;
      const shouldFilterYears = !yearsSelected.includes(this.ALL_TOKEN) && yearsSelected.length > 0;

      console.log('DEBUG: Row data:', row);
      console.log('DEBUG: Should filter states:', shouldFilterStates, 'Should filter years:', shouldFilterYears);

      // Apply state filter only if not "All" selected
      if (shouldFilterStates) {
        const rowState = this.normalizeForCompare('States', row.state);
        console.log('DEBUG: Row state:', rowState, 'States set:', sets.States);
        if (!sets.States.has(rowState)) {
          console.log('DEBUG: Filtered out by state:', rowState);
          return false;
        }
      }

      // Apply year filter only if not "All" selected
      if (shouldFilterYears) {
        const rowYear = this.normalizeForCompare('Years', row.year);
        console.log('DEBUG: Row year:', rowYear, 'Years set:', sets.Years);
        if (!sets.Years.has(rowYear)) {
          console.log('DEBUG: Filtered out by year:', rowYear);
          return false;
        }
      }

      const keywordMatch = this.rowMatchesKeyword(row, q);
      console.log('DEBUG: Keyword match:', keywordMatch, 'Query:', q);

      return keywordMatch;
    });

    console.log('DEBUG: Final filtered count:', this.filteredRowData.length);
  }

  onRowSelected(event: RowSelectedEvent): void {
    this.selectedRows = this.agGrid.api.getSelectedRows();
    const currentRow = event.node.data;
    console.log('Selected row:', currentRow);
  }

  get showSelectedDocumentsMenu() {
    return this.selectedRows.length > 0;
  }

  /**
   * Convert DocRow[] to CommonDimensions[] for the selected documents menu
   */
  getSelectedDocumentsForMenu(): CommonDimensions[] {
    return this.selectedRows.map(row => ({
      company: row.company || '',
      state: row.state || '',
      year: row.year?.toString() || '',
      title: row.title || ''
    }));
  }

  /**
   * Open clone documents dialog and handle the clone operation
   * Requirements: 1.1, 4.1, 4.2, 4.3, 5.4
   */
  openCloneDialog(): void {
    // Validate selection
    if (this.selectedRows.length === 0) {
      this.snackBar.open('Please select documents to clone', 'Close', { duration: 3000 });
      return;
    }

    // Check for maximum selection limit
    if (this.selectedRows.length > 100) {
      this.snackBar.open('Maximum 100 documents can be cloned at once. Please select fewer documents.', 'Close', {
        duration: 5000
      });
      return;
    }

    // Show loading state
    this.isLoading = true;

    // Fetch collections for the dialog
    this.collectionsService.getCollections().subscribe({
      next: (collections: Collection[]) => {
        this.isLoading = false;

        // Filter out hidden collections for non-admin users
        const availableCollections = collections.filter(collection => !collection.is_hidden);

        if (availableCollections.length === 0) {
          this.snackBar.open('No collections available for cloning. Please contact your administrator.', 'Close', {
            duration: 5000
          });
          return;
        }

        const dialogData: CloneDialogData = {
          collections: availableCollections,
          selectedDocuments: this.selectedRows
        };

        const dialogRef = this.dialog.open(CloneDocumentsDialogComponent, {
          width: '500px',
          maxWidth: '90vw',
          data: dialogData,
          disableClose: false
        });

        dialogRef.afterClosed().subscribe((result: CloneDialogResult) => {
          if (result && result.selectedCollectionId) {
            this.executeCloneOperation(result.selectedCollectionId, result.selectedCollection.name);
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        console.error('Error loading collections:', error);
        this.snackBar.open('Error loading collections. Please try again.', 'Close', {
          duration: 5000
        });
      }
    });
  }

  /**
   * Execute the clone operation using the libraries service
   * Requirements: 1.1, 1.2, 1.3, 5.4
   */
  private executeCloneOperation(targetCollectionId: string, targetCollectionName: string): void {
    // Extract actual document IDs from selected rows
    // Use the actual document IDs from the API response instead of generating synthetic ones
    // Debug logging to understand what data we have
    console.log('DEBUG: Selected rows for cloning:', this.selectedRows);
    console.log('DEBUG: First selected row structure:', this.selectedRows[0]);

    const documentIds = this.selectedRows
      .map(row => {
        // Prefer documentId, fallback to fileId if available
        const id = row.documentId || row.fileId || '';
        console.log(
          `DEBUG: Row ${row.title} -> documentId: '${row.documentId}', fileId: '${row.fileId}', using: '${id}'`
        );
        return id;
      })
      .filter(id => id !== ''); // Remove any empty IDs

    console.log('DEBUG: Extracted document IDs:', documentIds);

    // Validate that we have valid document IDs
    if (documentIds.length === 0) {
      console.error('No valid document IDs found in selected rows');
      console.error('Selected rows data:', this.selectedRows);
      this.snackBar.open('Error: No valid documents selected for cloning', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (documentIds.length !== this.selectedRows.length) {
      console.warn('Some selected documents do not have valid IDs', {
        selectedCount: this.selectedRows.length,
        validIdCount: documentIds.length
      });
    }

    // Show loading state
    this.isLoading = true;

    this.librariesService.cloneDocumentsToCollection(documentIds, targetCollectionId).subscribe({
      next: response => {
        this.isLoading = false;

        // Handle partial success (some documents failed)
        const failedCount = response.failed_documents ? response.failed_documents.length : 0;

        // Show success notification
        let message: string;
        let panelClass: string[] = ['success-snackbar'];

        if (failedCount && failedCount > 0) {
          message = `${response.cloned_count} document${response.cloned_count !== 1 ? 's' : ''} cloned to ${targetCollectionName}. ${failedCount} failed.`;
          panelClass = ['warning-snackbar'];
        } else {
          message = `Successfully cloned ${response.cloned_count} document${response.cloned_count !== 1 ? 's' : ''} to ${targetCollectionName}`;
        }

        this.snackBar.open(message, 'Close', {
          duration: 5000,
          panelClass
        });

        // If there were failures, log them for debugging
        if (response.failed_documents && response.failed_documents.length > 0) {
          console.warn('Some documents failed to clone:', response.failed_documents);

          // Show detailed error information in console for debugging
          response.failed_documents.forEach(failure => {
            console.warn(`Document ${failure.document_id} failed: ${failure.error}`);
          });
        }

        // Clear selection after clone operation (successful or partial)
        if (this.agGrid?.api) {
          this.agGrid.api.deselectAll();
        }
        this.selectedRows = [];
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;

        console.error('Error cloning documents:', error);

        // Provide specific error messages based on error status
        let errorMessage = 'Error cloning documents. Please try again.';
        if (error.status === 403) {
          errorMessage = 'You do not have permission to clone documents to this collection.';
        } else if (error.status === 404) {
          errorMessage = 'Collection or documents not found. Please refresh and try again.';
        } else if (error.status === 400) {
          errorMessage = 'Invalid request. Please check your selection and try again.';
        } else if (error.status === 0) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }

        this.snackBar.open(errorMessage, 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });

        // Don't clear selection on error - user might want to retry
      }
    });
  }

  clearAllFilters(): void {
    this.filterTypes.forEach(key => {
      this.selectedFilters[key] = [];
    });

    // Search box
    this.gridFilterText = '';

    if (this.agGrid?.api) {
      this.agGrid.api.setFilterModel(null);
      this.agGrid.api.setGridOption('quickFilterText', '');
    }

    this.applyFiltersFromUI?.();

    this.pendingFilterChanges = false;
    this.columnFiltersDirty = false;
  }

  areTopFiltersValid(): boolean {
    return this.filterTypes.every(ft => {
      const sel = this.selectedFilters[ft] ?? [];
      // require at least one REAL selection OR the ALL token
      return sel.length > 0;
    });
  }

  applyFiltersFromUI(): void {
    if (!this.areTopFiltersValid()) {
      return;
    }
    this.applyFilters();
    this.pendingFilterChanges = false;

    if (this.agGrid?.api) {
      const model = this.agGrid.api.getFilterModel() ?? {};
      this.columnFiltersDirty = Object.keys(model).length > 0;
    }
  }

  // Collections Search box
  clearInputText() {
    this.collectionInput = null;
    this.filteredCollections = [...this.collections];
  }

  displayFn(collectionId: string): string {
    const col = this.collections.find(c => c.collection_id == collectionId);
    return col ? col.name : '';
  }

  filterCollections(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();

    // Filter collections based on the input value
    this.filteredCollections = this.collections.filter(collection =>
      collection.name.toLowerCase().includes(filterValue)
    );

    // If no collections match and we have a selected collection, add it to the filtered list
    if (this.filteredCollections.length === 0 && this.selectedCollection) {
      if (this.selectedCollection.name.toLowerCase().includes(filterValue)) {
        this.filteredCollections = [this.selectedCollection];
      }
    }
  }

  selectTopCollection(event: Event): void {
    event.preventDefault();

    // If there are filtered collections, select the first one
    if (this.filteredCollections.length > 0) {
      // const topCollection = this.filteredCollections[0];
      // this.setCollection(topCollection);
      console.log('Top collection selected:', this.filteredCollections[0]);

      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
    }
  }

  getCollections() {
    const selectedCollectionId = this.selectedCollection?.collection_id;
    this.collectionsService.getCollections(true).subscribe({
      next: data => {
        this.collections = data;
        // Sort collections by name in alphabetical order
        this.filteredCollections = [...this.collections]; // Initialize filtered collections
        let lastActiveCollection = this.collections.find(c => c.collection_id === selectedCollectionId);
        if (!lastActiveCollection) {
          // No session found, set default to top collection
          lastActiveCollection = this.collections[0];
        }
        // this.setCollection(lastActiveCollection);
      },
      error: error => {
        console.error('There was an error while fetching the collections: ', error);
      }
    });
  }

  addCondition(): void {
    const newConditionIndex = this.conditionsFormArray.length;
    this.conditionsFormArray.push(this.createConditionFormGroup());
    setTimeout(() => {
      this.cleanInvalidClassesForCondition(newConditionIndex);
    }, 100);
  }

  removeCondition(index: number): void {
    if (this.conditionsFormArray.length > 1) {
      this.conditionsFormArray.removeAt(index);
      const logicControl = this.forwardingRuleForm.get('logic_operator');
      if (this.conditionsFormArray.length === 1) {
        logicControl?.setValue('');
        logicControl?.clearValidators();
        logicControl?.updateValueAndValidity();
      } else {
        logicControl?.updateValueAndValidity();
      }
    }
  }

  trackByIndex(i: number): number {
    return i;
  }

  // Data will be loaded from API
  allRowData: DocRow[] = [];

  filteredRowData: DocRow[] = this.allRowData.slice();

  // Search input binding
  gridFilterText = '';

  onQuickFilterChanged(value: string) {
    this.gridFilterText = value;
    if (this.agGrid?.api) {
      this.agGrid.api.updateGridOptions({ quickFilterText: this.gridFilterText });
    }
  }

  private conditionalLogicValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) {
        return null; // No validar si no hay parent form
      }

      const conditionsArray = control.parent.get('conditions') as FormArray;
      const logicOperatorValue = control.value;

      if (!conditionsArray) {
        return null;
      }

      const conditionsCount = conditionsArray.length;

      // Si hay 2 o mÃ¡s condiciones, logic_operator es requerido
      if (conditionsCount >= 2) {
        if (!logicOperatorValue || logicOperatorValue.trim() === '') {
          return { logicRequired: true };
        }
      }

      // Si hay menos de 2 condiciones, logic_operator no es necesario
      return null;
    };
  }
}
